import { api, handleApiError } from '../client'
import type {
  BrandGuideline,
  BrandGuidelineDetail,
  FontRule,
  ColorRule,
  LogoRule,
  StyleRule,
  ComplianceReport,
  CreateBrandGuidelineRequest,
  UpdateBrandGuidelineRequest,
  CreateFontRuleRequest,
  UpdateFontRuleRequest,
  CreateColorRuleRequest,
  UpdateColorRuleRequest,
  CreateLogoRuleRequest,
  UpdateLogoRuleRequest,
  CreateStyleRuleRequest,
  UpdateStyleRuleRequest,
  CheckComplianceRequest,
} from '../types'

export const brandComplianceService = {
  // Brand Guidelines

  /**
   * Gets all brand guidelines. Optionally filters by tenant.
   */
  async getGuidelines(tenantId?: string): Promise<BrandGuideline[]> {
    try {
      const params = tenantId ? { tenantId } : undefined
      const response = await api.get<BrandGuideline[]>('/v1/brand-guidelines', { params })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a specific brand guideline by ID with all rules.
   */
  async getGuideline(id: string): Promise<BrandGuidelineDetail> {
    try {
      const response = await api.get<BrandGuidelineDetail>(`/v1/brand-guidelines/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new brand guideline.
   */
  async createGuideline(data: CreateBrandGuidelineRequest): Promise<BrandGuideline> {
    try {
      const response = await api.post<BrandGuideline>('/v1/brand-guidelines', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing brand guideline.
   */
  async updateGuideline(id: string, data: UpdateBrandGuidelineRequest): Promise<BrandGuideline> {
    try {
      const response = await api.put<BrandGuideline>(`/v1/brand-guidelines/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a brand guideline.
   */
  async deleteGuideline(id: string): Promise<void> {
    try {
      await api.delete(`/v1/brand-guidelines/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Font Rules

  /**
   * Adds a font rule to a guideline.
   */
  async addFontRule(guidelineId: string, data: CreateFontRuleRequest): Promise<FontRule> {
    try {
      const response = await api.post<FontRule>(
        `/v1/brand-guidelines/${guidelineId}/font-rules`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates a font rule.
   */
  async updateFontRule(
    guidelineId: string,
    ruleId: string,
    data: UpdateFontRuleRequest
  ): Promise<FontRule> {
    try {
      const response = await api.put<FontRule>(
        `/v1/brand-guidelines/${guidelineId}/font-rules/${ruleId}`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a font rule.
   */
  async deleteFontRule(guidelineId: string, ruleId: string): Promise<void> {
    try {
      await api.delete(`/v1/brand-guidelines/${guidelineId}/font-rules/${ruleId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Color Rules

  /**
   * Adds a color rule to a guideline.
   */
  async addColorRule(guidelineId: string, data: CreateColorRuleRequest): Promise<ColorRule> {
    try {
      const response = await api.post<ColorRule>(
        `/v1/brand-guidelines/${guidelineId}/color-rules`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates a color rule.
   */
  async updateColorRule(
    guidelineId: string,
    ruleId: string,
    data: UpdateColorRuleRequest
  ): Promise<ColorRule> {
    try {
      const response = await api.put<ColorRule>(
        `/v1/brand-guidelines/${guidelineId}/color-rules/${ruleId}`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a color rule.
   */
  async deleteColorRule(guidelineId: string, ruleId: string): Promise<void> {
    try {
      await api.delete(`/v1/brand-guidelines/${guidelineId}/color-rules/${ruleId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Logo Rules

  /**
   * Adds a logo rule to a guideline.
   */
  async addLogoRule(guidelineId: string, data: CreateLogoRuleRequest): Promise<LogoRule> {
    try {
      const response = await api.post<LogoRule>(
        `/v1/brand-guidelines/${guidelineId}/logo-rules`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates a logo rule.
   */
  async updateLogoRule(
    guidelineId: string,
    ruleId: string,
    data: UpdateLogoRuleRequest
  ): Promise<LogoRule> {
    try {
      const response = await api.put<LogoRule>(
        `/v1/brand-guidelines/${guidelineId}/logo-rules/${ruleId}`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a logo rule.
   */
  async deleteLogoRule(guidelineId: string, ruleId: string): Promise<void> {
    try {
      await api.delete(`/v1/brand-guidelines/${guidelineId}/logo-rules/${ruleId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Style Rules

  /**
   * Adds a style rule to a guideline.
   */
  async addStyleRule(guidelineId: string, data: CreateStyleRuleRequest): Promise<StyleRule> {
    try {
      const response = await api.post<StyleRule>(
        `/v1/brand-guidelines/${guidelineId}/style-rules`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates a style rule.
   */
  async updateStyleRule(
    guidelineId: string,
    ruleId: string,
    data: UpdateStyleRuleRequest
  ): Promise<StyleRule> {
    try {
      const response = await api.put<StyleRule>(
        `/v1/brand-guidelines/${guidelineId}/style-rules/${ruleId}`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a style rule.
   */
  async deleteStyleRule(guidelineId: string, ruleId: string): Promise<void> {
    try {
      await api.delete(`/v1/brand-guidelines/${guidelineId}/style-rules/${ruleId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Compliance Checking

  /**
   * Runs compliance check on a template.
   */
  async checkCompliance(
    templateId: string,
    request?: CheckComplianceRequest
  ): Promise<ComplianceReport> {
    try {
      const response = await api.post<ComplianceReport>(
        `/v1/templates/${templateId}/compliance/check`,
        request || {}
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets the latest compliance report for a template.
   */
  async getComplianceReport(templateId: string): Promise<ComplianceReport | null> {
    try {
      const response = await api.get<ComplianceReport>(
        `/v1/templates/${templateId}/compliance/report`
      )
      return response.data
    } catch (error) {
      // Return null if no report exists
      return null
    }
  },

  /**
   * Gets compliance history for a template.
   */
  async getComplianceHistory(templateId: string): Promise<ComplianceReport[]> {
    try {
      const response = await api.get<ComplianceReport[]>(
        `/v1/templates/${templateId}/compliance/history`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
